import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import {
  formatDepositDate,
  formatMinorCurrency,
  getDepositCurrencySuffix,
  getOtherActivityMinor,
  negateMinor,
} from './-presentation'
import { DepositStatus, ReconciliationStatus } from './-status'

const moneyColumn = (id, label, value) => ({
  id,
  accessorFn: value,
  header: label,
  meta: { label, kind: 'money', align: 'end' },
  cell: ({ getValue, row }) => (
    <Typography
      variant={row.original.isTotal ? 'labelStrong' : 'bodySmall'}
      className="tabular-nums"
    >
      {getValue() == null ? 'Unavailable' : formatMinorCurrency(getValue(), row.original.currency)}
    </Typography>
  ),
})

export function getDepositColumns({
  path,
  orgId,
  salesOrderId,
  showPayoutFees = false,
  showInstantPayouts = false,
  showManualPayouts = false,
  showDisputes = false,
  showOtherActivity = false,
}) {
  return [
    {
      accessorKey: 'effective_deposit_at',
      header: 'Deposit Date',
      meta: { label: 'Deposit Date', kind: 'date' },
      cell: ({ row }) =>
        row.original.isTotal ? (
          <div className="space-y-1">
            <Typography variant="labelStrong" className="block">
              Period total{getDepositCurrencySuffix(row.original.currency)}
            </Typography>
            <Typography variant="caption" className="block">
              Completed deposits, all pages
            </Typography>
          </div>
        ) : (
          <div className="space-y-1">
            <Link {...getDepositLink(path, orgId, row.original.id, salesOrderId)} className="block">
              <Typography variant="label" tone="interaction" interaction="hoverUnderline">
                {formatDepositDate(row.original.effective_deposit_at)}
              </Typography>
            </Link>
            {getDepositDateCaption(row.original) ? (
              <Typography variant="caption" className="block">
                {getDepositDateCaption(row.original)}
              </Typography>
            ) : null}
          </div>
        ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: { label: 'Status', kind: 'badge' },
      cell: ({ row }) =>
        row.original.isTotal ? null : (
          <DepositStatus status={row.original.status} movementKind={row.original.movement_kind} />
        ),
    },
    moneyColumn('collected_minor', 'Card collections', (row) => row.collected_minor),
    moneyColumn('onestem_fees_minor', 'OneStem fees', (row) => negateMinor(row.onestem_fees_minor)),
    moneyColumn('merchant_fees_minor', 'Merchant fees', (row) =>
      negateMinor(row.merchant_fees_minor)
    ),
    // Most florists never have these, so their columns appear only when a deposit does.
    ...(showPayoutFees
      ? [
          moneyColumn('payout_fees_minor', 'Instant Payout fees', (row) =>
            negateMinor(row.payout_fees_minor)
          ),
        ]
      : []),
    ...(showInstantPayouts
      ? [
          moneyColumn(
            'instant_payouts_minor',
            'Instant Payouts',
            (row) => row.instant_payouts_minor
          ),
        ]
      : []),
    ...(showManualPayouts
      ? [moneyColumn('manual_payouts_minor', 'Manual payouts', (row) => row.manual_payouts_minor)]
      : []),
    ...(showDisputes
      ? [moneyColumn('dispute_activity_minor', 'Disputes', (row) => row.dispute_activity_minor)]
      : []),
    ...(showOtherActivity
      ? [moneyColumn('account_activity_minor', 'Other activity', getOtherActivityMinor)]
      : []),
    moneyColumn('amount_minor', 'Net deposit', (row) => row.amount_minor),
    {
      accessorKey: 'destination',
      header: 'Destination',
      meta: { label: 'Destination', kind: 'primary' },
      cell: ({ row }) =>
        row.original.isTotal
          ? null
          : (row.original.destination?.summary ?? 'Bank account not recorded'),
    },
    {
      accessorKey: 'entries_sync_status',
      header: 'Review',
      meta: { label: 'Review', kind: 'badge' },
      cell: ({ row }) =>
        row.original.isTotal ? null : row.original.paid_out_early ? (
          <Badge variant="outline" size="status">
            {row.original.method === 'instant' ? 'Instant Payout' : 'Manual payout'}
          </Badge>
        ) : row.original.status === 'paid' && row.original.needs_review ? (
          <ReconciliationStatus
            status={row.original.entries_sync_status}
            differenceMinor={row.original.reconciliation_difference_minor}
          />
        ) : null,
    },
  ]
}

function getDepositLink(path, orgId, depositId, salesOrderId) {
  const result = path.deposits.show(depositId).using({ orgId })

  return salesOrderId ? result.search({ sales_order_id: salesOrderId }).link : result.link
}

function getDepositDateCaption(deposit) {
  if (deposit.effective_date_source === 'failed_at_fallback') return 'Failure date'
  if (deposit.effective_date_source === 'canceled_at_fallback') return 'Cancellation date'
  if (deposit.effective_date_source !== 'expected_arrival') return 'Estimated'

  return null
}
